import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/use-toast';
import { apiService } from '@/services/api';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Building2, 
  Mail, 
  Phone, 
  MapPin,
  Star,
  Check,
  AlertCircle
} from 'lucide-react';

interface OperatorLead {
  id: number;
  preview_token: string;
  data: {
    user: { 
      name: string; 
      email: string; 
      phone: string; 
    };
    operator: { 
      company_name: string; 
      description: string; 
      address: string; 
    };
    location: { 
      name: string; 
      type: string; 
      features: string[];
      description?: string;
    };
  };
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  decision_at?: string;
}

interface OperatorPreviewProps {
  mock?: boolean;
}

const OperatorPreview: React.FC<OperatorPreviewProps> = ({ mock }) => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [lead, setLead] = useState<OperatorLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState<'pending' | 'processing' | 'completed'>('pending');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mock) {
      setLead({
        id: 1,
        preview_token: 'mocktoken',
        data: {
          user: { name: 'Jane Doe', email: 'jane@saunabliss.com', phone: '+353 87 123 4567' },
          operator: { company_name: 'Sauna Bliss', description: 'The best lakeside sauna in Ireland.', address: '123 Main St, Dublin' },
          location: { name: 'Sauna Bliss - Lakeside', type: 'Mobile Sauna', features: ['Wood-fired', 'Mobile', 'Lake Access'], description: 'Enjoy a lakeside sauna experience.' }
        },
        status: 'pending',
        created_at: new Date().toISOString(),
      });
      setLoading(false);
      return;
    }
    if (token) {
      fetchLeadPreview(token);
    }
  }, [token, mock]);

  const fetchLeadPreview = async (token: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getOperatorPreviewByToken(token);
      setLead(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load preview');
      console.error('Error fetching preview:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (decision: 'yes' | 'no') => {
    if (!token) return;
    
    setDecision('processing');
    try {
      await apiService.makeOperatorDecision(token, decision);
      setDecision('completed');
      toast({
        title: decision === 'yes' ? 'Approved!' : 'Rejected',
        description: decision === 'yes' 
          ? 'Your business listing has been approved and will be published soon.' 
          : 'Your business listing has been rejected.',
      });
      await fetchLeadPreview(token);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to process decision',
        variant: 'destructive',
      });
      setDecision('pending');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
        <div className="text-center text-white">
          <Spinner className="w-8 h-8 mx-auto mb-4" />
          <p>Loading your business preview...</p>
        </div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center text-white">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-200" />
          <h1 className="text-2xl font-bold mb-2">Preview Not Found</h1>
          <p className="mb-6 opacity-90">{error || 'The preview link is invalid or has expired.'}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="text-white border-white hover:bg-white hover:text-blue-600">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Business Preview - SaunaDip</title>
        <meta name="description" content="Review your business listing preview on SaunaDip" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-600">
        {/* Header */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm py-12 text-center text-white">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Welcome to SaunaDip! 🧖‍♀️</h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              We've discovered your business and created a beautiful preview of how it will appear on our platform.
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Status Display */}
          {lead.status !== 'pending' && (
            <div className="mb-8">
              <Alert className="max-w-2xl mx-auto">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex items-center justify-between">
                    <span>This preview has been {lead.status}</span>
                    {getStatusBadge(lead.status)}
                  </div>
                  {lead.decision_at && (
                    <p className="text-sm text-gray-600 mt-1">
                      Decision made on {new Date(lead.decision_at).toLocaleDateString()}
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Business Information Card */}
            <Card className="bg-white/95 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Your Business Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem label="Business Name" value={lead.data.operator.company_name} />
                  <InfoItem label="Contact Person" value={lead.data.user.name} />
                  <InfoItem label="Email" value={lead.data.user.email} icon={<Mail className="w-4 h-4" />} />
                  <InfoItem label="Phone" value={lead.data.user.phone} icon={<Phone className="w-4 h-4" />} />
                  <InfoItem label="Address" value={lead.data.operator.address} icon={<MapPin className="w-4 h-4" />} fullWidth />
                  <InfoItem label="Description" value={lead.data.operator.description} fullWidth />
                </div>
              </CardContent>
            </Card>

            {/* Location Preview Card */}
            <Card className="bg-white/95 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Location Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem label="Location Name" value={lead.data.location.name} />
                  <InfoItem label="Type" value={lead.data.location.type} />
                  {lead.data.location.description && (
                    <InfoItem label="Description" value={lead.data.location.description} fullWidth />
                  )}
                </div>
                
                {lead.data.location.features && lead.data.location.features.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                    <div className="flex flex-wrap gap-2">
                      {lead.data.location.features.map((feature, index) => (
                        <Badge key={index} variant="secondary" className="bg-blue-100 text-blue-800">
                          <Check className="w-3 h-3 mr-1" />
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Decision Section */}
            {lead.status === 'pending' && (
              <Card className="bg-white/95 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>What would you like to do?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-6">
                    If this information looks correct, we can publish your listing immediately. 
                    If you'd like to make changes, you can contact us or reject this preview.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      onClick={() => handleDecision('yes')}
                      disabled={decision === 'processing'}
                      className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg"
                    >
                      {decision === 'processing' ? (
                        <>
                          <Spinner className="w-4 h-4 mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Yes, publish my listing
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      onClick={() => handleDecision('no')}
                      disabled={decision === 'processing'}
                      variant="destructive"
                      className="px-8 py-4 text-lg"
                    >
                      {decision === 'processing' ? (
                        <>
                          <Spinner className="w-4 h-4 mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 mr-2" />
                          No, don't publish
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            <Card className="bg-white/95 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Need Help?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  If you have questions about your listing or need to make changes, please contact us:
                </p>
                <div className="space-y-2">
                  <p><strong>Email:</strong> support@saunadip.com</p>
                  <p><strong>Phone:</strong> +353 1 234 5678</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

// Helper component for displaying info items
interface InfoItemProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, icon, fullWidth = false }) => (
  <div className={fullWidth ? 'col-span-full' : ''}>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="flex items-center gap-2 text-gray-900">
      {icon}
      <span>{value}</span>
    </div>
  </div>
);

export default OperatorPreview; 